import { sharedApiGet } from "./shared-api-data";
import { installDeferredApiLoading } from "./deferred-api-loading";
import { installServerPagination } from "./server-pagination";
import { joinApiUrl } from "../../config/api-url";
import { Formio } from "formiojs";

const FieldComponent =
    Formio.Components.components.field;

/* =========================================================
 * Allgemeine Hilfsfunktionen
 * ========================================================= */

/**
 * Liest einen Wert über einen Punktpfad.
 *
 * Beispiele:
 * getByPath(data, "selectedGroup.id")
 * getByPath(response, "data.permissions")
 */
function getByPath(object, path) {
    if (!path) {
        return object;
    }

    return String(path)
        .split(".")
        .filter(Boolean)
        .reduce((current, key) => {
            if (
                current === null ||
                current === undefined
            ) {
                return undefined;
            }

            return current[key];
        }, object);
}

/**
 * Schreibt einen Wert über einen Punktpfad.
 *
 * Beispiel:
 * setByPath(payload, "context.targetId", 5)
 */
function setByPath(object, path, value) {
    const parts = String(path || "")
        .split(".")
        .filter(Boolean);

    if (parts.length === 0) {
        return;
    }

    let current = object;

    parts.forEach((part, index) => {
        if (index === parts.length - 1) {
            current[part] = value;
            return;
        }

        if (
            current[part] === null ||
            current[part] === undefined ||
            typeof current[part] !== "object"
        ) {
            current[part] = {};
        }

        current = current[part];
    });
}

/**
 * Ersetzt Platzhalter in einem Endpoint.
 *
 * Beispiel:
 * /api/context/{scopeType}/{targetId}
 */
function resolveTemplate(template, data) {
    return String(template || "").replace(
        /\{([^}]+)\}/g,
        (match, key) => {
            const value = getByPath(
                data,
                key.trim()
            );

            if (
                value === null ||
                value === undefined
            ) {
                return "";
            }

            return encodeURIComponent(
                String(value)
            );
        }
    );
}

/**
 * Erstellt eine tiefe Kopie einfacher JSON-Daten.
 */
function deepClone(value) {
    if (
        value === null ||
        value === undefined
    ) {
        return value;
    }

    return JSON.parse(
        JSON.stringify(value)
    );
}

/**
 * Ergänzt ausschließlich fehlende Konfigurationswerte.
 * Bereits im Form.io-Builder gespeicherte Werte haben immer Vorrang.
 * Arrays werden nur gesetzt, wenn die Eigenschaft vollständig fehlt;
 * vorhandene Arrays werden niemals automatisch zusammengeführt.
 */
function isPlainObject(value) {
    return (
        value !== null &&
        typeof value === "object" &&
        !Array.isArray(value)
    );
}

function mergeMissingDefaults(target, defaults) {
    if (!isPlainObject(target) || !isPlainObject(defaults)) {
        return target;
    }

    Object.keys(defaults).forEach((key) => {
        const defaultValue = defaults[key];

        if (target[key] === undefined) {
            target[key] = deepClone(defaultValue);
            return;
        }

        if (
            isPlainObject(target[key]) &&
            isPlainObject(defaultValue)
        ) {
            mergeMissingDefaults(target[key], defaultValue);
        }
    });

    return target;
}


/**
 * Vergleicht zwei einfache JSON-Datenstrukturen.
 * Wird für die Änderungsverfolgung einzelner Zeilen verwendet.
 */
function valuesAreEqual(left, right) {
    try {
        return JSON.stringify(left) === JSON.stringify(right);
    } catch (error) {
        return false;
    }
}

/**
 * Wandelt Arrays, Strings oder Tags-Werte
 * in ein String-Array um.
 */
function normalizeList(value) {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return [];
    }

    let sourceValues;

    if (Array.isArray(value)) {
        sourceValues = value;
    } else if (typeof value === "string") {
        sourceValues = value.split(",");
    } else {
        sourceValues = [value];
    }

    return sourceValues
        .map((entry) => {
            if (
                entry &&
                typeof entry === "object"
            ) {
                if (
                    entry.value !== null &&
                    entry.value !== undefined
                ) {
                    return String(
                        entry.value
                    ).trim();
                }

                if (
                    entry.label !== null &&
                    entry.label !== undefined
                ) {
                    return String(
                        entry.label
                    ).trim();
                }
            }

            return String(entry).trim();
        })
        .filter(Boolean);
}

/**
 * Liest abhängige Felder.
 *
 * Neue Konfiguration:
 * api.dependsOnText = "scopeType, targetId"
 *
 * Alte Array-Konfiguration bleibt kompatibel.
 */
function normalizeDependsOn(config) {
    if (
        typeof config.dependsOnText === "string" &&
        config.dependsOnText.trim() !== ""
    ) {
        return normalizeList(
            config.dependsOnText
        );
    }

    return normalizeList(
        config.dependsOn
    );
}

function normalizeQueryParams(config) {
    return Array.isArray(
        config && config.queryParams
    ) ? config.queryParams : [];
}

/**
 * Liest zusätzliche Kontextfelder für das Speicher-Payload.
 */
function normalizeSaveContextFields(config) {
    if (
        typeof config.saveContextFieldsText === "string" &&
        config.saveContextFieldsText.trim() !== ""
    ) {
        return normalizeList(
            config.saveContextFieldsText
        );
    }

    return normalizeList(
        config.saveContextFields
    );
}

/**
 * Liefert true, wenn ein Wert vorhanden ist.
 *
 * Die Zahl 0 und false gelten als vorhandene Werte.
 */
function hasValue(value) {
    return (
        value !== null &&
        value !== undefined &&
        value !== ""
    );
}

/**
 * HTML-Sonderzeichen maskieren.
 */
function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

/**
 * API-/Axios-Fehler in eine lesbare Meldung umwandeln.
 */
function normalizeAxiosError(error) {
    const responseData =
        error &&
        error.response &&
        error.response.data ?
        error.response.data :
        null;

    if (responseData) {
        if (responseData.messageDe) {
            return responseData.messageDe;
        }

        if (responseData.Message) {
            return responseData.Message;
        }

        if (responseData.message) {
            return responseData.message;
        }

        if (typeof responseData === "string") {
            return responseData;
        }
    }

    return (
        error ?.message ||
        "Die Daten konnten nicht verarbeitet werden."
    );
}

/**
 * Vergleicht zwei Werte.
 */
function valuesMatch(
    currentValue,
    expectedValue,
    ignoreCase
) {
    if (
        currentValue === null ||
        currentValue === undefined
    ) {
        return false;
    }

    const left =
        String(currentValue);

    const right =
        String(expectedValue ?? "");

    if (ignoreCase === true) {
        return (
            left.toLowerCase() ===
            right.toLowerCase()
        );
    }

    return left === right;
}

/* =========================================================
 * Generische Query-Parameter
 *
 * Quellen:
 * - form        -> Form.io-Gesamtdaten
 * - community   -> window.communityData
 * - currentUser -> window.currentUser
 * - window      -> window
 * - literal     -> definition.value
 * ========================================================= */
function getQueryParamValue(definition, formData) {
    if (!definition || typeof definition !== "object") {
        return undefined;
    }

    const source = String(definition.source || "form")
        .trim()
        .toLowerCase();

    const path = String(definition.path || "").trim();

    switch (source) {
        case "literal":
            return definition.value;
        case "window":
            return path ? getByPath(window, path) : undefined;
        case "currentuser":
        case "user":
            return path ? getByPath(window.currentUser || {}, path) : undefined;
        case "community":
            return path ? getByPath(window.communityData || {}, path) : undefined;
        case "form":
        default:
            return path ? getByPath(formData || {}, path) : undefined;
    }
}

function appendQueryParams(endpoint, definitions, formData, options = {}) {
    let result = String(endpoint || "");
    const params = Array.isArray(definitions) ? definitions : [];
    const throwOnMissingRequired = options.throwOnMissingRequired !== false;

    params.forEach((definition) => {
        if (!definition || typeof definition !== "object") return;

        const name = String(definition.name || "").trim();
        if (!name) return;

        const value = getQueryParamValue(definition, formData);

        if (!hasValue(value)) {
            if (definition.required === true && throwOnMissingRequired) {
                throw new Error(`Query-Parameter '${name}' konnte nicht aufgelöst werden.`);
            }
            return;
        }

        result +=
            (result.includes("?") ? "&" : "?") +
            encodeURIComponent(name) +
            "=" +
            encodeURIComponent(String(value));
    });

    return result;
}

/* =========================================================
 * ApiDataGridComponent
 * ========================================================= */



const API_DATA_GRID_CONFIG_VERSION = 3;

function getApiDataGridConfigDefaults() {
    return {
        paginationEnabled: true,
        pageSize: 10,
        pageSizeOptionsText: "5, 10, 25, 50",

        searchEnabled: false,
        searchPlaceholder: "Einträge durchsuchen",
        searchFieldsText: "",

        filtersEnabled: false,
        filtersCollapsible: true,
        filtersInitiallyOpen: false,
        showResetFiltersButton: true,
        filterDefinitions: [],

        sortingEnabled: true,
        defaultSortField: "",
        defaultSortDirection: "asc",
        reloadOnDataChanged: false,

        showDirtyCount: true,
        showResetChangesButton: true,
        highlightChangedRows: true,

        emptyMessage: "Keine Einträge vorhanden.",
        noFilterResultMessage: "Keine passenden Einträge gefunden.",
        loadingMessage: "Daten werden geladen …",
        savingMessage: "Änderungen werden gespeichert …",
        saveButtonLabel: "Änderungen speichern",
        resetChangesButtonLabel: "Änderungen verwerfen",

        api: {
            loadEndpoint: "",
            loadMethod: "GET",
            loadBody: null,
            resultPath: "",

            saveEndpoint: "",
            saveMethod: "PUT",
            savePayloadPath: "",

            dependsOnText: "",
            saveContextFieldsText: "",
            dependsOn: [],
            saveContextFields: [],

            contextMappings: [],
            queryParams: [],
            nullValue: -1,
            autoLoad: true
        }
    };
}

export default class ApiDataGridComponent
extends FieldComponent {

    static schema(...extend) {
        return FieldComponent.schema({
                type: "apiDataGrid",
                label: "API Data Grid",
                key: "apiDataGrid",

                input: true,
                persistent: false,
                tableView: false,

                api: {
                    loadEndpoint: "",
                    loadMethod: "GET",
                    loadBody: null,

                    resultPath: "",

                    saveEndpoint: "",
                    saveMethod: "PUT",
                    savePayloadPath: "",

                    /*
                     * Neue Textkonfiguration.
                     */
                    dependsOnText: "",
                    saveContextFieldsText: "",

                    /*
                     * Alte Array-Konfiguration bleibt unterstützt.
                     */
                    dependsOn: [],
                    saveContextFields: [],

                    /*
                     * Generische Kontextzuordnungen.
                     *
                     * Beispiel:
                     * [
                     *   {
                     *     outputField: "targetId",
                     *     sourceField: "selectedGroupId",
                     *     whenField: "scopeType",
                     *     whenValue: "group",
                     *     onlyWhenEmpty: true
                     *   }
                     * ]
                     */
                    contextMappings: [],

                    /*
                     * Generische Query-Parameter für Lade-Requests.
                     */
                    queryParams: [],

                    nullValue: -1,

                    autoLoad: true
                },

                columns: [],

                editable: false,
                editField: "permissionLevel",
                editLabel: "Berechtigung",

                editOptions: [{
                        label: "Nicht sichtbar",
                        value: 0
                    },
                    {
                        label: "Nur sichtbar",
                        value: 1
                    },
                    {
                        label: "Bearbeitbar",
                        value: 2
                    }
                ],

                /*
                 * 1. Pagination
                 */
                paginationEnabled: true,
                pageSize: 10,
                pageSizeOptionsText: "5, 10, 25, 50",

                /*
                 * 2. Suche
                 */
                searchEnabled: false,
                searchPlaceholder: "Einträge durchsuchen",
                searchFieldsText: "",

                /*
                 * 3. Konfigurierbare Filter
                 */
                filtersEnabled: false,
                filtersCollapsible: true,
                filtersInitiallyOpen: false,
                showResetFiltersButton: true,
                filterDefinitions: [],

                /*
                 * 4. Sortierung
                 */
                sortingEnabled: true,
                defaultSortField: "",
                defaultSortDirection: "asc",
                reloadOnDataChanged: false,

                /*
                 * 5–7. Änderungsverwaltung
                 */
                showDirtyCount: true,
                showResetChangesButton: true,
                highlightChangedRows: true,

                /*
                 * 8. Status- und Leertexte
                 */
                emptyMessage: "Keine Einträge vorhanden.",
                noFilterResultMessage: "Keine passenden Einträge gefunden.",
                loadingMessage: "Daten werden geladen …",
                savingMessage: "Änderungen werden gespeichert …",
                saveButtonLabel: "Änderungen speichern",
                resetChangesButtonLabel: "Änderungen verwerfen"
            },
            ...extend
        );
    }

    static get builderInfo() {
        return {
            title: "API Data Grid",
            icon: "table",
            group: "basic",
            weight: 24,
            schema: ApiDataGridComponent.schema()
        };
    }

    constructor(
        component,
        options,
        data
    ) {
        super(
            component,
            options,
            data
        );

        this._rows = [];

        this._apiLoading = false;
        this._apiSaving = false;

        this._apiLoadedSignature = null;
        this._apiError = null;

        this._dirty = false;
        this._originalRows = [];
        this._dirtyRowIndexes = new Set();

        this._searchValue = "";
        this._filterValues = {};
        this._filtersOpen =
            this.component.filtersInitiallyOpen === true;

        this._currentPage = 1;
        this._sortField =
            this.component.defaultSortField || "";
        this._sortDirection =
            this.component.defaultSortDirection === "desc" ?
            "desc" :
            "asc";

        this._dataChangedHandler = null;
        this._rootChangeHandler = null;
        this._dependencyChangeTimer = null;
        this._loadingPromise = null;
        installServerPagination(this, "data");
    }

    init() {
        this.upgradeComponentConfig();
        super.init();
    }

    /*
     * Migriert bereits gespeicherte Alt-Konfigurationen schrittweise.
     * Vorhandene Builder-Werte werden niemals überschrieben.
     */
    upgradeComponentConfig() {
        const component = this.component || {};

        if (!isPlainObject(component.api)) {
            component.api = {};
        }

        let version = Number(
            component.apiDataGridConfigVersion || 0
        );

        // Bekannte Alt-Eigenschaft immer bereinigen, auch wenn ein älterer
        // Build bereits einen Versionsmarker gespeichert hat.
        if (
            component.reloadOnDataChanged === undefined &&
            component.reloadOnPackageChanged !== undefined
        ) {
            component.reloadOnDataChanged =
                component.reloadOnPackageChanged === true;
        }

        if (
            Object.prototype.hasOwnProperty.call(
                component,
                "reloadOnPackageChanged"
            )
        ) {
            delete component.reloadOnPackageChanged;
        }

        // v0 -> v1:
        // Domänenspezifisches reloadOnPackageChanged wurde durch den
        // generischen Datenänderungs-Mechanismus ersetzt.
        if (version < 1) {
            version = 1;
        }

        // v1 -> v2:
        // Generische Query-Parameter wurden ergänzt.
        if (version < 2) {
            if (component.api.queryParams === undefined) {
                component.api.queryParams = [];
            }

            version = 2;
        }

        // Aktuelle Defaults werden bei jedem Laden geprüft. Dadurch werden
        // auch unvollständig gespeicherte Konfigurationen mit bereits gesetztem
        // Versionsmarker repariert. Vorhandene Werte bleiben unangetastet.
        mergeMissingDefaults(
            component,
            getApiDataGridConfigDefaults()
        );

        if (version < 3) {
            version = 3;
        }

        component.apiDataGridConfigVersion =
            Math.max(version, API_DATA_GRID_CONFIG_VERSION);
    }

    get apiConfig() {
        return this.component.api || {};
    }

    get isBuilderMode() {
        return !!(
            window.__OZG_FORMIO_BUILDER_MODE === true ||
            (this.options && this.options.builder === true) ||
            (this.root && this.root.options && this.root.options.builder === true)
        );
    }

    /* =======================================================
     * Formulardaten und Kontext
     * ======================================================= */

    /**
     * Liefert die aktuellen Daten des gesamten Formulars.
     */
    getFormData() {
        if (
            this.root &&
            this.root.data &&
            typeof this.root.data === "object"
        ) {
            return this.root.data;
        }

        if (
            this.root &&
            this.root.submission &&
            this.root.submission.data &&
            typeof this.root.submission.data === "object"
        ) {
            return this.root.submission.data;
        }

        return this.data || {};
    }

    /**
     * Prüft, ob eine Kontextzuordnung aufgrund
     * ihrer Bedingung angewendet werden darf.
     */
    mappingConditionMatches(
        mapping,
        contextData
    ) {
        const whenField =
            String(
                mapping.whenField || ""
            ).trim();

        /*
         * Kein Bedingungsfeld:
         * Zuordnung immer anwenden.
         */
        if (!whenField) {
            return true;
        }

        const currentValue =
            getByPath(
                contextData,
                whenField
            );

        const expectedValue =
            mapping.whenValue;

        const ignoreCase =
            mapping.ignoreCase !== false;

        return valuesMatch(
            currentValue,
            expectedValue,
            ignoreCase
        );
    }

    /**
     * Wendet alle konfigurierten Feldzuordnungen an.
     *
     * Jede Zuordnung kann:
     * - ein Quellfeld lesen,
     * - den Wert in ein Zielfeld schreiben,
     * - optional nur bei einer Bedingung gelten,
     * - optional nur schreiben, wenn das Zielfeld leer ist.
     */
    applyContextMappings(formData) {
        const contextData =
            deepClone(
                formData || {}
            ) || {};

        const mappings =
            Array.isArray(
                this.apiConfig.contextMappings
            ) ?
            this.apiConfig.contextMappings : [];

        mappings.forEach((mapping) => {
            if (!mapping) {
                return;
            }

            const outputField =
                String(
                    mapping.outputField || ""
                ).trim();

            const sourceField =
                String(
                    mapping.sourceField || ""
                ).trim();

            if (!outputField ||
                !sourceField
            ) {
                return;
            }

            if (!this.mappingConditionMatches(
                    mapping,
                    contextData
                )) {
                return;
            }

            const currentOutputValue =
                getByPath(
                    contextData,
                    outputField
                );

            const onlyWhenEmpty =
                mapping.onlyWhenEmpty !== false;

            if (
                onlyWhenEmpty &&
                hasValue(currentOutputValue)
            ) {
                return;
            }

            const sourceValue =
                getByPath(
                    contextData,
                    sourceField
                );

            if (
                mapping.copyEmptyValue !== true &&
                !hasValue(sourceValue)
            ) {
                return;
            }

            setByPath(
                contextData,
                outputField,
                deepClone(sourceValue)
            );
        });

        return contextData;
    }

    /**
     * Liefert die Formulardaten inklusive
     * aller konfigurierten Feldzuordnungen.
     */
    getResolvedContextData() {
        return this.applyContextMappings(
            this.getFormData()
        );
    }

    /**
     * Erzeugt eine Signatur aus Endpoint,
     * HTTP-Methode und Abhängigkeiten.
     */
    getResolvedLoadEndpoint(throwOnMissingRequired = true) {
        const config = this.apiConfig;
        const contextData = this.getResolvedContextData();

        const baseEndpoint = resolveTemplate(
            config.loadEndpoint,
            contextData
        );

        return appendQueryParams(
            baseEndpoint,
            normalizeQueryParams(config),
            contextData,
            { throwOnMissingRequired }
        );
    }

    get requestSignature() {
        const config = this.apiConfig;
        const contextData = this.getResolvedContextData();
        const dependsOn = normalizeDependsOn(config);

        let endpoint;
        try {
            endpoint = this.getResolvedLoadEndpoint(false);
        } catch (error) {
            endpoint = resolveTemplate(config.loadEndpoint, contextData);
        }

        const queryParamState = normalizeQueryParams(config)
            .map((definition) => {
                if (!definition) return null;
                return {
                    name: definition.name || "",
                    source: definition.source || "form",
                    path: definition.path || "",
                    required: definition.required === true,
                    value: getQueryParamValue(definition, contextData)
                };
            })
            .filter(Boolean);

        return JSON.stringify({
            endpoint,
            method: String(config.loadMethod || "GET").toUpperCase(),
            dependencies: dependsOn.map((key) => ({
                key,
                value: getByPath(contextData, key)
            })),
            queryParams: queryParamState
        });
    }

    /**
     * Prüft, ob die API geladen werden darf.
     */
    shouldLoad() {
        if (this.isBuilderMode) {
            return false;
        }


        const config =
            this.apiConfig;

        if (
            config.autoLoad === false ||
            !config.loadEndpoint
        ) {
            return false;
        }

        const contextData =
            this.getResolvedContextData();

        const dependsOn =
            normalizeDependsOn(config);

        const dependenciesReady = dependsOn.every((key) => {
            return hasValue(
                getByPath(
                    contextData,
                    key
                )
            );
        });

        if (!dependenciesReady) {
            return false;
        }

        return normalizeQueryParams(config)
            .filter((definition) => definition && definition.required === true)
            .every((definition) => {
                return hasValue(
                    getQueryParamValue(
                        definition,
                        contextData
                    )
                );
            });
    }

    /* =======================================================
     * API
     * ======================================================= */

    getAxios() {
        const axios =
            window.GLOBALAXIOS;

        if (!axios) {
            throw new Error(
                "GLOBALAXIOS ist nicht verfügbar."
            );
        }

        return axios;
    }

    getApiBaseUrl() {
        return (
            window.GLOBALCONFIG ?.API_URL ||
            ""
        );
    }

    async executeRequest(
        method,
        endpoint,
        payload
    ) {
        // Letzte zentrale Schranke: Im Form.io Builder niemals HTTP ausführen.
        if (this.isBuilderMode) {
            console.debug(
                "[ApiDataGrid] API-Aufruf im Builder blockiert:",
                endpoint
            );

            return {
                status: 200,
                data: []
            };
        }

        const axios =
            this.getAxios();

        const url =
            joinApiUrl(this.getApiBaseUrl(), endpoint);

        const normalizedMethod =
            String(
                method || "GET"
            ).toUpperCase();

        switch (normalizedMethod) {
            case "POST":
                return axios.post(
                    url,
                    payload || {}
                );

            case "PUT":
                return axios.put(
                    url,
                    payload || {}
                );

            case "PATCH":
                return axios.patch(
                    url,
                    payload || {}
                );

            case "DELETE":
                return axios.delete(
                    url, {
                        data: payload || undefined
                    }
                );

            case "GET":
            default:
                return sharedApiGet(axios, url);
        }
    }

    /* =======================================================
     * Darstellung
     * ======================================================= */

    render() {
        return super.render(`
      <div class="api-data-grid">

        <div
          ref="statusRef"
          class="mb-2"
        ></div>

        <div
          ref="controlsRef"
          class="api-data-grid-controls mb-3"
        >
          ${this.renderControls()}
        </div>

        <div
          class="table-responsive"
        >
          <table
            class="table table-striped table-bordered table-hover"
          >
            <thead ref="headRef"></thead>
            <tbody ref="bodyRef"></tbody>
          </table>
        </div>

        <div
          ref="paginationRef"
          class="mt-2"
        ></div>

        <div
          ref="footerRef"
          class="mt-3"
        >
          ${this.renderFooter()}
        </div>
      </div>
    `);
    }

    renderControls() {
            const searchHtml =
                this.component.searchEnabled === true ?
                `
          <div class="col-12 col-md-5 col-lg-4 mb-3">
            <label class="mb-1">
              Suche
            </label>

            <input
              type="search"
              ref="searchRef"
              class="form-control"
              placeholder="${escapeHtml(
                this.component.searchPlaceholder ||
                "Einträge durchsuchen"
              )}"
              value="${escapeHtml(
                this._searchValue || ""
              )}"
            >
          </div>
        ` :
                "";

            const pageSizeOptions =
                this.getPageSizeOptions();

            const pageSizeHtml =
                this.component.paginationEnabled !== false &&
                pageSizeOptions.length > 1 ?
                `
          <div class="col-12 col-md-3 col-lg-2 mb-3">
            <label class="mb-1">
              Einträge pro Seite
            </label>

            <select
              ref="pageSizeRef"
              class="form-control"
            >
              ${pageSizeOptions
                .map((value) => `
                  <option
                    value="${escapeHtml(value)}"
                    ${
                      Number(value) === this.getPageSize()
                        ? " selected"
                        : ""
                    }
                  >
                    ${escapeHtml(value)}
                  </option>
                `)
                .join("")}
            </select>
          </div>
        `
        : "";

    const definitions =
      this.getFilterDefinitions();

    const filterHtml =
      this.component.filtersEnabled === true &&
      definitions.length > 0
        ? `
          <div class="col-12">
            ${
              this.component.filtersCollapsible !== false
                ? `
                  <button
                    type="button"
                    ref="filterToggleRef"
                    class="btn btn-outline-primary btn-sm mb-3"
                  >
                    <i class="fa ${
                      this._filtersOpen
                        ? "fa-chevron-up"
                        : "fa-chevron-down"
                    } mr-1"></i>

                    ${
                      this._filtersOpen
                        ? "Filter ausblenden"
                        : "Filter anzeigen"
                    }

                    ${
                      this.getActiveFilterCount() > 0
                        ? `
                          <span class="badge badge-primary ml-1">
                            ${this.getActiveFilterCount()}
                          </span>
                        `
                        : ""
                    }
                  </button>
                `
                : ""
            }

            <div
              ref="filterPanelRef"
              style="${
                this.component.filtersCollapsible !== false &&
                !this._filtersOpen
                  ? "display:none;"
                  : ""
              }"
            >
              <div class="card mb-3">
                <div class="card-body">
                  <div class="row">
                    ${definitions
                      .map((definition, index) =>
                        this.renderFilterControl(
                          definition,
                          index
                        )
                      )
                      .join("")}
                  </div>

                  ${
                    this.component.showResetFiltersButton !== false
                      ? `
                        <button
                          type="button"
                          ref="resetFiltersRef"
                          class="btn btn-outline-secondary btn-sm"
                        >
                          <i class="fa fa-times mr-1"></i>
                          Filter zurücksetzen
                        </button>
                      `
                      : ""
                  }
                </div>
              </div>
            </div>
          </div>
        `
        : "";

    if (
      !searchHtml &&
      !pageSizeHtml &&
      !filterHtml
    ) {
      return "";
    }

    return `
      <div class="row align-items-end">
        ${searchHtml}
        ${pageSizeHtml}
        ${filterHtml}
      </div>
    `;
  }

  renderFooter() {
    if (!this.component.editable) {
      return "";
    }

    const dirtyCount =
      this._dirtyRowIndexes.size;

    const dirtyText =
      this.component.showDirtyCount !== false
        ? `
          <span
            ref="dirtyCountRef"
            class="mr-3"
          >
            ${
              dirtyCount > 0
                ? `${dirtyCount} Änderung${
                    dirtyCount === 1 ? "" : "en"
                  } noch nicht gespeichert`
                : "Keine offenen Änderungen"
            }
          </span>
        `
        : "";

    const saveButton =
      this.apiConfig.saveEndpoint
        ? `
          <button
            type="button"
            class="btn btn-primary mr-2"
            ref="saveRef"
            ${dirtyCount === 0 ? "disabled" : ""}
          >
            ${escapeHtml(
              this.component.saveButtonLabel ||
              "Änderungen speichern"
            )}
          </button>
        `
        : "";

    const resetButton =
      this.component.showResetChangesButton !== false
        ? `
          <button
            type="button"
            class="btn btn-outline-secondary"
            ref="resetChangesRef"
            ${dirtyCount === 0 ? "disabled" : ""}
          >
            ${escapeHtml(
              this.component.resetChangesButtonLabel ||
              "Änderungen verwerfen"
            )}
          </button>
        `
        : "";

    return `
      <div class="d-flex flex-wrap align-items-center">
        ${dirtyText}
        ${saveButton}
        ${resetButton}
      </div>
    `;
  }

  attach(element) {
    const attached =
      super.attach(element);

    this.loadRefs(element, {
      bodyRef: "single",
      headRef: "single",
      statusRef: "single",
      controlsRef: "single",
      footerRef: "single",
      paginationRef: "single",
      searchRef: "single",
      pageSizeRef: "single",
      filterToggleRef: "single",
      filterPanelRef: "single",
      resetFiltersRef: "single",
      saveRef: "single",
      resetChangesRef: "single",
      dirtyCountRef: "single"
    });

    this.bindControls();
    this.bindFooterButtons();

    this.updateStatus();
    this.renderRows();

    window.setTimeout(() => {
      if (!this.isBuilderMode) {
        this.checkDependenciesAndLoad();
      }
    }, 0);

    if (
      !this.isBuilderMode &&
      this.root &&
      typeof this.root.on === "function"
    ) {
      this._rootChangeHandler =
        () => {
          window.clearTimeout(
            this._dependencyChangeTimer
          );

          this._dependencyChangeTimer =
            window.setTimeout(() => {
              this.checkDependenciesAndLoad();
            }, 75);
        };

      this.root.on(
        "change",
        this._rootChangeHandler
      );
    }

    if (
      !this.isBuilderMode &&
      this.component.reloadOnDataChanged === true &&
      this.root &&
      typeof this.root.on === "function" &&
      !this._dataChangedHandler
    ) {
      this._dataChangedHandler = (eventData) => {
        if (eventData && eventData.source === this.component.key) {
          return;
        }

        this._apiLoadedSignature = null;
        window.setTimeout(() => this.loadRows(true), 0);
      };

      this.root.on("ozgApiDataChanged", this._dataChangedHandler);
    }

    return attached;
  }

  bindControls() {
    if (this.refs.searchRef) {
      this.addEventListener(
        this.refs.searchRef,
        "input",
        (event) => {
          this._searchValue =
            event.target.value || "";

          this._currentPage = 1;
          this.renderRows();
        }
      );
    }

    if (this.refs.pageSizeRef) {
      this.addEventListener(
        this.refs.pageSizeRef,
        "change",
        (event) => {
          const pageSize =
            Number(event.target.value);

          if (pageSize > 0) {
            this.component.pageSize =
              pageSize;

            this._currentPage = 1;
            this.renderRows();
          }
        }
      );
    }

    if (this.refs.filterToggleRef) {
      this.addEventListener(
        this.refs.filterToggleRef,
        "click",
        () => {
          this._filtersOpen =
            !this._filtersOpen;

          this.redraw();
        }
      );
    }

    if (this.refs.resetFiltersRef) {
      this.addEventListener(
        this.refs.resetFiltersRef,
        "click",
        () => {
          this._filterValues = {};
          this._currentPage = 1;
          this.redraw();
        }
      );
    }

    if (this.refs.filterPanelRef) {
      this.refs.filterPanelRef
        .querySelectorAll(
          "[data-filter-key]"
        )
        .forEach((input) => {
          const eventName =
            input.tagName === "INPUT" &&
            [
              "text",
              "search",
              "number",
              "date"
            ].includes(input.type)
              ? "input"
              : "change";

          this.addEventListener(
            input,
            eventName,
            () => {
              this.readFilterValue(
                input
              );

              this._currentPage = 1;
              this.renderRows();
            }
          );
        });
    }
  }

  bindFooterButtons() {
    if (this.refs.saveRef) {
      this.addEventListener(
        this.refs.saveRef,
        "click",
        () => this.saveRows()
      );
    }

    if (this.refs.resetChangesRef) {
      this.addEventListener(
        this.refs.resetChangesRef,
        "click",
        () => this.resetChanges()
      );
    }
  }

  updateStatus() {
    if (!this.refs.statusRef) {
      return;
    }

    if (this._apiLoading) {
      this.refs.statusRef.innerHTML = `
        <div class="alert alert-info">
          ${escapeHtml(
            this.component.loadingMessage ||
            "Daten werden geladen …"
          )}
        </div>
      `;

      return;
    }

    if (this._apiSaving) {
      this.refs.statusRef.innerHTML = `
        <div class="alert alert-info">
          ${escapeHtml(
            this.component.savingMessage ||
            "Änderungen werden gespeichert …"
          )}
        </div>
      `;

      return;
    }

    if (this._apiError) {
      this.refs.statusRef.innerHTML = `
        <div class="alert alert-danger">
          ${escapeHtml(
            this._apiError
          )}
        </div>
      `;

      return;
    }

    this.refs.statusRef.innerHTML = "";
  }

  /**
   * Liefert die konfigurierten Anzeigeoptionen einer Spalte.
   *
   * useEditOptionsForDisplay = true:
   * Die globalen editOptions des Grids werden auch für die
   * reine Tabellenanzeige verwendet.
   */
  getColumnDisplayOptions(column) {
    if (
      !column ||
      column.useEditOptionsForDisplay !== true
    ) {
      return [];
    }

    return Array.isArray(
      this.component.editOptions
    )
      ? this.component.editOptions
      : [];
  }

  /**
   * Wandelt einen technischen Spaltenwert in ein Label um.
   * Die API-/Formulardaten selbst werden dabei nicht verändert.
   */
  formatColumnCellValue(
    value,
    column
  ) {
    if (
      !hasValue(value) &&
      column &&
      hasValue(column.emptyDisplayLabel)
    ) {
      return String(
        column.emptyDisplayLabel
      );
    }

    const options =
      this.getColumnDisplayOptions(
        column
      );

    if (
      options.length > 0 &&
      hasValue(value)
    ) {
      const match =
        options.find((option) => {
          return (
            option &&
            String(option.value) ===
              String(value)
          );
        });

      if (
        match &&
        hasValue(match.label)
      ) {
        return String(match.label);
      }
    }

    return this.formatCellValue(
      value
    );
  }

  formatCellValue(value) {
    if (
      value === null ||
      value === undefined
    ) {
      return "";
    }

    if (typeof value === "boolean") {
      return value
        ? "Ja"
        : "Nein";
    }

    if (Array.isArray(value)) {
      return value.join(", ");
    }

    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }

    return String(value);
  }

  getPageSizeOptions() {
    const values =
      normalizeList(
        this.component.pageSizeOptionsText ||
        "5, 10, 25, 50"
      );

    const parsed =
      values
        .map((value) => Number(value))
        .filter((value) => value > 0);

    return Array.from(
      new Set(parsed)
    );
  }

  getPageSize() {
    const configured =
      Number(this.component.pageSize);

    return configured > 0
      ? configured
      : 10;
  }

  getSearchFields() {
    const configured =
      normalizeList(
        this.component.searchFieldsText
      );

    if (configured.length > 0) {
      return configured;
    }

    const columns =
      Array.isArray(
        this.component.columns
      )
        ? this.component.columns
        : [];

    return columns
      .map((column) =>
        column && column.field
          ? String(column.field)
          : ""
      )
      .filter(Boolean);
  }

  getFilterDefinitions() {
    const definitions =
      Array.isArray(
        this.component.filterDefinitions
      )
        ? this.component.filterDefinitions
        : [];

    return definitions.filter((definition) => {
      return (
        definition &&
        String(
          definition.key || ""
        ).trim() !== "" &&
        String(
          definition.field || ""
        ).trim() !== ""
      );
    });
  }

  getActiveFilterCount() {
    return Object.values(
      this._filterValues || {}
    ).filter((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (
        value &&
        typeof value === "object"
      ) {
        return Object.values(
          value
        ).some(hasValue);
      }

      return hasValue(value);
    }).length;
  }

  renderFilterControl(
    definition,
    index
  ) {
    const key =
      String(definition.key);

    const value =
      this._filterValues[key];

    const type =
      definition.type || "text";

    const label =
      definition.label ||
      definition.field;

    const common =
      `data-filter-key="${escapeHtml(key)}"`;

    let control = "";

    if (
      type === "select" ||
      type === "multiselect"
    ) {
      const options =
        this.getFilterOptions(
          definition
        );

      const selected =
        Array.isArray(value)
          ? value.map(String)
          : [String(value ?? "")];

      control = `
        <select
          class="form-control"
          ${common}
          ${
            type === "multiselect"
              ? "multiple"
              : ""
          }
        >
          ${
            type === "select"
              ? '<option value="">Alle</option>'
              : ""
          }

          ${options
            .map((option) => {
              const optionValue =
                String(
                  option.value ?? ""
                );

              return `
                <option
                  value="${escapeHtml(optionValue)}"
                  ${
                    selected.includes(
                      optionValue
                    )
                      ? " selected"
                      : ""
                  }
                >
                  ${escapeHtml(
                    option.label ??
                    optionValue
                  )}
                </option>
              `;
            })
            .join("")}
        </select>
      `;
    } else if (type === "boolean") {
      control = `
        <select
          class="form-control"
          ${common}
        >
          <option value="">Alle</option>
          <option
            value="true"
            ${
              value === true
                ? " selected"
                : ""
            }
          >
            Ja
          </option>
          <option
            value="false"
            ${
              value === false
                ? " selected"
                : ""
            }
          >
            Nein
          </option>
        </select>
      `;
    } else if (
      type === "numberRange" ||
      type === "dateRange"
    ) {
      const current =
        value &&
        typeof value === "object"
          ? value
          : {};

      const inputType =
        type === "dateRange"
          ? "date"
          : "number";

      const fromLabel =
        definition.fromLabel ||
        "Mindestens";

      const toLabel =
        definition.toLabel ||
        "Höchstens";

      control = `
        <div class="row">
          <div class="col-6">
            <label class="small mb-1">
              ${escapeHtml(fromLabel)}
            </label>

            <input
              type="${inputType}"
              class="form-control"
              data-filter-key="${escapeHtml(key)}"
              data-filter-part="from"
              title="${escapeHtml(
                definition.fromTitle ||
                "Untergrenze des Filterbereichs"
              )}"
              value="${escapeHtml(
                current.from ?? ""
              )}"
            >
          </div>

          <div class="col-6">
            <label class="small mb-1">
              ${escapeHtml(toLabel)}
            </label>

            <input
              type="${inputType}"
              class="form-control"
              data-filter-key="${escapeHtml(key)}"
              data-filter-part="to"
              title="${escapeHtml(
                definition.toTitle ||
                "Obergrenze des Filterbereichs"
              )}"
              value="${escapeHtml(
                current.to ?? ""
              )}"
            >
          </div>
        </div>
      `;
    } else {
      const inputType =
        type === "number"
          ? "number"
          : type === "date"
            ? "date"
            : "text";

      control = `
        <input
          type="${inputType}"
          class="form-control"
          ${common}
          value="${escapeHtml(
            value ?? ""
          )}"
          placeholder="${escapeHtml(
            definition.placeholder ||
            ""
          )}"
        >
      `;
    }

    return `
      <div
        class="col-md-4 mb-3"
        data-filter-index="${index}"
      >
        <label>
          ${escapeHtml(label)}
        </label>

        ${control}
      </div>
    `;
  }

  getFilterOptions(definition) {
    /*
    * Fest konfigurierte Auswahlwerte.
    */
    if (
      typeof definition.valuesText ===
        "string" &&
      definition.valuesText.trim() !== ""
    ) {
      return normalizeList(
        definition.valuesText
      ).map((value) => ({
        value,
        label: value
      }));
    }

    /*
    * Automatische Auswahlwerte aus den
    * vollständig geladenen API-Zeilen.
    */
    const rows =
      Array.isArray(this._rows)
        ? this._rows
        : [];

    const unique =
      new Map();

    rows.forEach((row) => {
      const value =
        getByPath(
          row,
          definition.field
        );

      if (!hasValue(value)) {
        return;
      }

      const key =
        String(value);

      if (!unique.has(key)) {
        unique.set(key, {
          value,
          label: String(value)
        });
      }
    });

    return Array.from(
      unique.values()
    ).sort((left, right) => {
      return String(left.label)
        .localeCompare(
          String(right.label),
          "de",
          {
            numeric: true,
            sensitivity: "base"
          }
        );
    });
  }

  readFilterValue(input) {
    const key =
      input.dataset.filterKey;

    const part =
      input.dataset.filterPart;

    const definition =
      this.getFilterDefinitions()
        .find((item) =>
          String(item.key) ===
          String(key)
        );

    if (!definition) {
      return;
    }

    if (part) {
      const current =
        this._filterValues[key] &&
        typeof this._filterValues[key] ===
          "object"
          ? {
              ...this._filterValues[key]
            }
          : {};

      current[part] =
        input.value;

      this._filterValues[key] =
        current;

      return;
    }

    if (
      definition.type ===
      "multiselect"
    ) {
      this._filterValues[key] =
        Array.from(
          input.selectedOptions
        )
          .map((option) =>
            option.value
          )
          .filter(Boolean);

      return;
    }

    if (
      definition.type ===
      "boolean"
    ) {
      this._filterValues[key] =
        input.value === ""
          ? ""
          : input.value === "true";

      return;
    }

    this._filterValues[key] =
      input.value;
  }

  rowMatchesFilter(
    row,
    definition,
    filterValue
  ) {
    if (
      filterValue === null ||
      filterValue === undefined ||
      filterValue === ""
    ) {
      return true;
    }

    const currentValue =
      getByPath(
        row,
        definition.field
      );

    const ignoreCase =
      definition.ignoreCase !== false;

    switch (
      definition.type || "text"
    ) {
      case "select":
        return valuesMatch(
          currentValue,
          filterValue,
          ignoreCase
        );

      case "multiselect":
        return normalizeList(
          filterValue
        ).some((value) =>
          valuesMatch(
            currentValue,
            value,
            ignoreCase
          )
        );

      case "boolean":
        return (
          Boolean(currentValue) ===
          Boolean(filterValue)
        );

      case "number":
        return (
          Number(currentValue) ===
          Number(filterValue)
        );

      case "numberRange": {
        const number =
          Number(currentValue);

        if (Number.isNaN(number)) {
          return false;
        }

        if (
          hasValue(filterValue.from) &&
          number <
            Number(filterValue.from)
        ) {
          return false;
        }

        if (
          hasValue(filterValue.to) &&
          number >
            Number(filterValue.to)
        ) {
          return false;
        }

        return true;
      }

      case "date": {
        const current =
          new Date(currentValue);

        const expected =
          new Date(filterValue);

        return (
          !Number.isNaN(current.getTime()) &&
          !Number.isNaN(expected.getTime()) &&
          current.toISOString().slice(0, 10) ===
            expected.toISOString().slice(0, 10)
        );
      }

      case "dateRange": {
        const current =
          new Date(currentValue);

        if (
          Number.isNaN(
            current.getTime()
          )
        ) {
          return false;
        }

        if (hasValue(filterValue.from)) {
          const from =
            new Date(
              `${filterValue.from}T00:00:00`
            );

          if (current < from) {
            return false;
          }
        }

        if (hasValue(filterValue.to)) {
          const to =
            new Date(
              `${filterValue.to}T23:59:59.999`
            );

          if (current > to) {
            return false;
          }
        }

        return true;
      }

      case "text":
      default: {
        const left =
          currentValue === null ||
          currentValue === undefined
            ? ""
            : String(currentValue);

        const right =
          String(filterValue);

        const normalizedLeft =
          ignoreCase
            ? left.toLowerCase()
            : left;

        const normalizedRight =
          ignoreCase
            ? right.toLowerCase()
            : right;

        switch (
          definition.operator ||
          "contains"
        ) {
          case "equals":
            return (
              normalizedLeft ===
              normalizedRight
            );

          case "notEquals":
            return (
              normalizedLeft !==
              normalizedRight
            );

          case "startsWith":
            return normalizedLeft
              .startsWith(
                normalizedRight
              );

          case "endsWith":
            return normalizedLeft
              .endsWith(
                normalizedRight
              );

          case "contains":
          default:
            return normalizedLeft
              .includes(
                normalizedRight
              );
        }
      }
    }
  }

  getProcessedEntries() {
    const rows =
      Array.isArray(this._rows)
        ? this._rows
        : [];

    let entries =
      rows.map((row, originalIndex) => ({
        row,
        originalIndex
      }));

    const definitions =
      this.getFilterDefinitions();

    if (
      this.component.filtersEnabled ===
        true
    ) {
      entries =
        entries.filter((entry) => {
          return definitions.every(
            (definition) => {
              return this.rowMatchesFilter(
                entry.row,
                definition,
                this._filterValues[
                  definition.key
                ]
              );
            }
          );
        });
    }

    const searchValue =
      String(
        this._searchValue || ""
      )
        .trim()
        .toLowerCase();

    if (
      this.component.searchEnabled ===
        true &&
      searchValue !== ""
    ) {
      const fields =
        this.getSearchFields();

      entries =
        entries.filter((entry) => {
          return fields.some((field) => {
            return String(
              getByPath(
                entry.row,
                field
              ) ?? ""
            )
              .toLowerCase()
              .includes(searchValue);
          });
        });
    }

    if (
      this.component.sortingEnabled !==
        false &&
      this._sortField
    ) {
      const direction =
        this._sortDirection === "desc"
          ? -1
          : 1;

      entries.sort((left, right) => {
        const leftValue =
          getByPath(
            left.row,
            this._sortField
          );

        const rightValue =
          getByPath(
            right.row,
            this._sortField
          );

        if (
          leftValue === rightValue
        ) {
          return 0;
        }

        if (
          leftValue === null ||
          leftValue === undefined
        ) {
          return -1 * direction;
        }

        if (
          rightValue === null ||
          rightValue === undefined
        ) {
          return 1 * direction;
        }

        const leftNumber =
          Number(leftValue);

        const rightNumber =
          Number(rightValue);

        if (
          !Number.isNaN(leftNumber) &&
          !Number.isNaN(rightNumber)
        ) {
          return (
            leftNumber - rightNumber
          ) * direction;
        }

        return String(leftValue)
          .localeCompare(
            String(rightValue),
            "de",
            {
              numeric: true,
              sensitivity: "base"
            }
          ) * direction;
      });
    }

    return entries;
  }

  getPagedEntries(entries) {
    if (
      this.component.paginationEnabled ===
      false
    ) {
      return entries;
    }

    const pageSize =
      this.getPageSize();

    const pageCount =
      Math.max(
        1,
        Math.ceil(
          entries.length / pageSize
        )
      );

    if (
      this._currentPage >
      pageCount
    ) {
      this._currentPage =
        pageCount;
    }

    const start =
      (this._currentPage - 1) *
      pageSize;

    return entries.slice(
      start,
      start + pageSize
    );
  }

  renderTableHead() {
    if (!this.refs.headRef) {
      return;
    }

    const columns =
      Array.isArray(
        this.component.columns
      )
        ? this.component.columns
        : [];

    const headerHtml =
      columns
        .map((column) => {
          const field =
            column.field || "";

          const sortable =
            this.component.sortingEnabled !==
              false &&
            field !== "";

          const indicator =
            this._sortField === field
              ? this._sortDirection ===
                "desc"
                ? " ↓"
                : " ↑"
              : "";

          return `
            <th>
              ${
                sortable
                  ? `
                    <button
                      type="button"
                      class="btn btn-link p-0 font-weight-bold text-dark"
                      data-sort-field="${escapeHtml(field)}"
                    >
                      ${escapeHtml(
                        column.label ||
                        field
                      )}${indicator}
                    </button>
                  `
                  : escapeHtml(
                      column.label ||
                      field
                    )
              }
            </th>
          `;
        })
        .join("");

    const editableHeader =
      this.component.editable
        ? `
          <th>
            ${escapeHtml(
              this.component.editLabel ||
              "Bearbeitung"
            )}
          </th>
        `
        : "";

    this.refs.headRef.innerHTML = `
      <tr>
        ${headerHtml}
        ${editableHeader}
      </tr>
    `;

    this.refs.headRef
      .querySelectorAll(
        "[data-sort-field]"
      )
      .forEach((button) => {
        this.addEventListener(
          button,
          "click",
          () => {
            const field =
              button.dataset.sortField;

            if (
              this._sortField === field
            ) {
              this._sortDirection =
                this._sortDirection ===
                  "asc"
                  ? "desc"
                  : "asc";
            } else {
              this._sortField =
                field;

              this._sortDirection =
                "asc";
            }

            this._currentPage = 1;
            this.renderRows();
          }
        );
      });
  }

  refreshControls() {
    if (!this.refs.controlsRef) {
      return;
    }

    this.refs.controlsRef.innerHTML =
      this.renderControls();

    this.loadRefs(
      this.element,
      {
        searchRef: "single",
        pageSizeRef: "single",
        filterToggleRef: "single",
        filterPanelRef: "single",
        resetFiltersRef: "single"
      }
    );

    this.bindControls();
  }

  renderRows() {
    if (!this.refs.bodyRef) {
      return;
    }

    this.renderTableHead();

    const columns =
      Array.isArray(
        this.component.columns
      )
        ? this.component.columns
        : [];

    const options =
      Array.isArray(
        this.component.editOptions
      )
        ? this.component.editOptions
        : [];

    const processed =
      this.getProcessedEntries();

    const paged =
      this.getPagedEntries(
        processed
      );

    if (
      !Array.isArray(this._rows) ||
      this._rows.length === 0
    ) {
      this.refs.bodyRef.innerHTML = `
        <tr>
          <td
            colspan="${
              columns.length +
              (this.component.editable ? 1 : 0)
            }"
            class="text-center text-muted"
          >
            ${escapeHtml(
              this.component.emptyMessage ||
              "Keine Einträge vorhanden."
            )}
          </td>
        </tr>
      `;

      this.renderPagination(
        processed.length
      );
      this.refreshFooter();

      return;
    }

    if (processed.length === 0) {
      this.refs.bodyRef.innerHTML = `
        <tr>
          <td
            colspan="${
              columns.length +
              (this.component.editable ? 1 : 0)
            }"
            class="text-center text-muted"
          >
            ${escapeHtml(
              this.component.noFilterResultMessage ||
              "Keine passenden Einträge gefunden."
            )}
          </td>
        </tr>
      `;

      this.renderPagination(0);
      this.refreshFooter();

      return;
    }

    this.refs.bodyRef.innerHTML =
      paged
        .map((entry) => {
          const row =
            entry.row;

          const originalIndex =
            entry.originalIndex;

          const rowClass =
            this.component.highlightChangedRows !==
              false &&
            this._dirtyRowIndexes.has(
              originalIndex
            )
              ? "table-warning"
              : "";

          const cells =
            columns
              .map((column) => {
                const value =
                  getByPath(
                    row,
                    column.field
                  );

                return `
                  <td>
                    ${escapeHtml(
                      this.formatColumnCellValue(
                        value,
                        column
                      )
                    )}
                  </td>
                `;
              })
              .join("");

          let editCell = "";

          if (this.component.editable) {
            const rawCurrentValue =
              getByPath(
                row,
                this.component.editField
              );

            const currentValue =
              rawCurrentValue === null ||
              rawCurrentValue === undefined ||
              rawCurrentValue === ""
                ? this.apiConfig.nullValue
                : rawCurrentValue;

            const optionHtml =
              options
                .map((option) => {
                  const selected =
                    String(option.value) ===
                    String(currentValue)
                      ? " selected"
                      : "";

                  return `
                    <option
                      value="${escapeHtml(
                        option.value
                      )}"
                      ${selected}
                    >
                      ${escapeHtml(
                        option.label
                      )}
                    </option>
                  `;
                })
                .join("");

            editCell = `
              <td>
                <select
                  class="form-control"
                  data-original-index="${originalIndex}"
                >
                  ${optionHtml}
                </select>
              </td>
            `;
          }

          return `
            <tr class="${rowClass}">
              ${cells}
              ${editCell}
            </tr>
          `;
        })
        .join("");

    if (this.component.editable) {
      this.refs.bodyRef
        .querySelectorAll(
          "select[data-original-index]"
        )
        .forEach((select) => {
          this.addEventListener(
            select,
            "change",
            (event) => {
              const originalIndex =
                Number(
                  event.target.dataset
                    .originalIndex
                );

              const selectedValue =
                event.target.value;

              const matchingOption =
                options.find((option) =>
                  String(option.value) ===
                  String(selectedValue)
                );

              const newValue =
                matchingOption
                  ? matchingOption.value
                  : selectedValue;

              if (
                !this._rows[
                  originalIndex
                ]
              ) {
                return;
              }

              setByPath(
                this._rows[originalIndex],
                this.component.editField,
                newValue
              );

              const originalRow =
                this._originalRows[
                  originalIndex
                ];

              const isDirty =
                !valuesAreEqual(
                  this._rows[
                    originalIndex
                  ],
                  originalRow
                );

              if (isDirty) {
                this._dirtyRowIndexes.add(
                  originalIndex
                );
              } else {
                this._dirtyRowIndexes.delete(
                  originalIndex
                );
              }

              this._dirty =
                this._dirtyRowIndexes.size >
                0;

              this.dataValue =
                deepClone(
                  this._rows
                );

              super.updateValue(
                this.dataValue,
                {
                  modified: true,
                  noUpdateEvent: false
                }
              );

              this.renderRows();
            }
          );
        });
    }

    this.renderPagination(
      processed.length
    );

    this.refreshFooter();
  }

  renderPagination(totalCount) {
    if (!this.refs.paginationRef) {
      return;
    }

    if (
      this.component.paginationEnabled ===
        false
    ) {
      this.refs.paginationRef.innerHTML = "";
      return;
    }

    const pageSize =
      this.getPageSize();

    const pageCount =
      Math.max(
        1,
        Math.ceil(
          totalCount / pageSize
        )
      );

    const currentPage =
      Math.min(
        this._currentPage,
        pageCount
      );

    if (
      totalCount <= pageSize
    ) {
      this.refs.paginationRef.innerHTML =
        totalCount > 0
          ? `
            <small class="text-muted">
              ${totalCount} Einträge
            </small>
          `
          : "";

      return;
    }

    this.refs.paginationRef.innerHTML = `
      <div class="d-flex justify-content-between align-items-center">
        <small class="text-muted">
          Seite ${currentPage} von ${pageCount}
          · ${totalCount} Einträge
        </small>

        <div>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm mr-2"
            data-page-action="previous"
            ${currentPage <= 1 ? "disabled" : ""}
          >
            Zurück
          </button>

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            data-page-action="next"
            ${currentPage >= pageCount ? "disabled" : ""}
          >
            Weiter
          </button>
        </div>
      </div>
    `;

    this.refs.paginationRef
      .querySelectorAll(
        "[data-page-action]"
      )
      .forEach((button) => {
        this.addEventListener(
          button,
          "click",
          () => {
            const action =
              button.dataset.pageAction;

            if (
              action === "previous" &&
              this._currentPage > 1
            ) {
              this._currentPage -= 1;
            }

            if (
              action === "next" &&
              this._currentPage < pageCount
            ) {
              this._currentPage += 1;
            }

            this.renderRows();
          }
        );
      });
  }

  refreshFooter() {
    if (!this.refs.footerRef) {
      return;
    }

    this.refs.footerRef.innerHTML =
      this.renderFooter();

    this.loadRefs(
      this.element,
      {
        saveRef: "single",
        resetChangesRef: "single",
        dirtyCountRef: "single"
      }
    );

    this.bindFooterButtons();
  }

  resetChanges() {
    if (
      this._dirtyRowIndexes.size === 0
    ) {
      return;
    }

    this._rows =
      deepClone(
        this._originalRows
      );

    this.dataValue =
      deepClone(
        this._rows
      );

    this._dirty = false;
    this._dirtyRowIndexes.clear();

    super.updateValue(
      this.dataValue,
      {
        modified: true,
        noUpdateEvent: false
      }
    );

    this.renderRows();
  }

  /* =======================================================
   * Abhängigkeiten und Laden
   * ======================================================= */

  checkDependenciesAndLoad() {
    if (
      this._apiLoading ||
      this._apiSaving
    ) {
      return;
    }

    if (!this.shouldLoad()) {
      if (
        this._rows.length > 0 ||
        this._apiLoadedSignature !== null
      ) {
        this._rows = [];
        this.dataValue = [];

        this._dirty = false;
        this._apiLoadedSignature = null;
        this._apiError = null;

        this.renderRows();
        this.updateStatus();
      }

      return;
    }

    const signature =
      this.requestSignature;

    if (
      signature !==
        this._apiLoadedSignature &&
      !this._dirty
    ) {
      this.loadRows(true);
    }
  }

  async loadRows(force = false) {
    if (
      this._apiLoading ||
      this._apiSaving ||
      !this.shouldLoad()
    ) {
      return;
    }

    const signature =
      this.requestSignature;

    if (
      force !== true &&
      signature ===
        this._apiLoadedSignature
    ) {
      this.renderRows();
      return;
    }

    if (this._loadingPromise) {
      return this._loadingPromise;
    }

    const config =
      this.apiConfig;

    const contextData =
      this.getResolvedContextData();

    const endpoint =
      this.getResolvedLoadEndpoint(true);

    const method =
      String(
        config.loadMethod || "GET"
      ).toUpperCase();

    this._apiLoading = true;
    this._apiError = null;

    this.updateStatus();

    this._loadingPromise =
      this.executeRequest(
        method,
        endpoint,
        config.loadBody || {}
      )
        .then((response) => {
          const result =
            getByPath(
              response.data,
              config.resultPath
            );

          const rows =
            Array.isArray(result)
              ? deepClone(result)
              : [];

          this._rows = rows;
          this._originalRows =
            deepClone(rows);

          this.dataValue =
            deepClone(rows);

          this._dirty = false;
          this._dirtyRowIndexes.clear();
          this._currentPage = 1;

          this._apiLoadedSignature =
            signature;

          /*
          * Filter-Auswahllisten jetzt erneut aufbauen,
          * da this._rows erst nach dem API-Aufruf gefüllt ist.
          */
          this.refreshControls();
          this.renderRows();

          return rows;
        })
        .catch((error) => {
          this._rows = [];
          this._originalRows = [];
          this.dataValue = [];
          this._dirty = false;
          this._dirtyRowIndexes.clear();

          this._apiError =
            normalizeAxiosError(
              error
            );

          this._apiLoadedSignature =
            null;

          this.refreshControls();
          this.renderRows();

          console.warn(
            "[ApiDataGrid] Laden fehlgeschlagen",
            {
              key:
                this.component.key,
              endpoint,
              error
            }
          );

          return [];
        })
        .finally(() => {
          this._apiLoading = false;
          this._loadingPromise = null;

          this.updateStatus();
        });

    return this._loadingPromise;
  }

  /* =======================================================
   * Speichern
   * ======================================================= */

  buildSavePayload() {
    const config =
      this.apiConfig;

    const contextData =
      this.getResolvedContextData();

    const payload = {};

    const editField =
      this.component.editField;

    const nullValue =
      config.nullValue;

    /*
    * Kopie der Zeilen erzeugen.
    * Der konfigurierte Ersatzwert, beispielsweise -1,
    * wird vor dem API-Aufruf in null umgewandelt.
    */
    const rows =
      deepClone(
        this._rows
      ).map((row) => {
        const currentValue =
          getByPath(
            row,
            editField
          );

        if (
          nullValue !== null &&
          nullValue !== undefined &&
          String(currentValue) ===
            String(nullValue)
        ) {
          setByPath(
            row,
            editField,
            null
          );
        }

        return row;
      });

    if (config.savePayloadPath) {
      setByPath(
        payload,
        config.savePayloadPath,
        rows
      );
    } else {
      payload.rows = rows;
    }

    const contextFields =
      normalizeSaveContextFields(
        config
      );

    contextFields.forEach(
      (fieldKey) => {
        setByPath(
          payload,
          fieldKey,
          deepClone(
            getByPath(
              contextData,
              fieldKey
            )
          )
        );
      }
    );

    return payload;
  }

  emitDataChanged(action, payload = null) {
    if (
      this.root &&
      typeof this.root.emit === "function"
    ) {
      this.root.emit("ozgApiDataChanged", {
        source: this.component.key,
        action: action || "changed",
        payload: deepClone(payload)
      });
    }
  }

  async saveRows() {
    const config =
      this.apiConfig;

    if (!config.saveEndpoint) {
      this._apiError =
        "Es wurde kein Speicher-Endpoint konfiguriert.";

      this.updateStatus();
      return;
    }

    if (!this._dirty) {
      return;
    }

    if (
      this._apiLoading ||
      this._apiSaving
    ) {
      return;
    }

    const contextData =
      this.getResolvedContextData();

    const endpoint =
      resolveTemplate(
        config.saveEndpoint,
        contextData
      );

    const method =
      String(
        config.saveMethod || "PUT"
      ).toUpperCase();

    const payload =
      this.buildSavePayload();

    this._apiSaving = true;
    this._apiError = null;

    this.updateStatus();

    try {
      await this.executeRequest(
        method,
        endpoint,
        payload
      );

      this._dirty = false;
      this._dirtyRowIndexes.clear();

      /*
       * Nach dem Speichern erneut laden.
       */
      this._apiLoadedSignature = null;

      await this.loadRows(true);
      this.emitDataChanged("save", payload);
    } catch (error) {
      this._apiError =
        normalizeAxiosError(
          error
        );

      console.warn(
        "[ApiDataGrid] Speichern fehlgeschlagen",
        {
          key:
            this.component.key,
          endpoint,
          payload,
          error
        }
      );
    } finally {
      this._apiSaving = false;
      this.updateStatus();
    }
  }

  /* =======================================================
   * Form.io-Datenmodell
   * ======================================================= */

  updateValue(
    value,
    flags = {}
  ) {
    const changed =
      super.updateValue(
        value,
        flags
      );

    if (this.isBuilderMode) {
      return changed;
    }

    if (
      !this._dirty &&
      !this._apiLoading &&
      !this._apiSaving
    ) {
      window.clearTimeout(
        this._dependencyChangeTimer
      );

      this._dependencyChangeTimer =
        window.setTimeout(() => {
          this.checkDependenciesAndLoad();
        }, 50);
    }

    return changed;
  }

  getValue() {
    return deepClone(
      this._rows
    );
  }

  setValue(value, flags = {}) {
    if (!Array.isArray(value)) {
      return false;
    }

    /*
     * Alte Form.io-Submission-Werte nicht über
     * bereits geladene API-Werte schreiben.
     */
    if (
      this._apiLoadedSignature &&
      flags.fromSubmission === true
    ) {
      return false;
    }

    this._rows =
      deepClone(value);

    this._originalRows =
      deepClone(value);

    this.dataValue =
      deepClone(value);

    this._dirty = false;
    this._dirtyRowIndexes.clear();
    this._currentPage = 1;

    this.renderRows();

    return true;
  }

  reloadApiRows() {
    this._apiLoadedSignature = null;
    this._dirty = false;
    this._dirtyRowIndexes.clear();

    return this.loadRows(true);
  }

  destroy() {
    window.clearTimeout(
      this._dependencyChangeTimer
    );

    if (
      this.root &&
      this._rootChangeHandler &&
      typeof this.root.off === "function"
    ) {
      this.root.off(
        "change",
        this._rootChangeHandler
      );
    }

    this._rootChangeHandler = null;
    this._loadingPromise = null;

    if (
      this._dataChangedHandler &&
      this.root &&
      typeof this.root.off === "function"
    ) {
      this.root.off("ozgApiDataChanged", this._dataChangedHandler);
    }

    this._dataChangedHandler = null;

    return super.destroy();
  }
}

/* =========================================================
 * Konfiguration im Form.io-Editor
 * ========================================================= */

ApiDataGridComponent.editForm =
  function () {
    return {
      components: [
        {
          type: "tabs",
          key: "tabs",
          components: [
            {
              key: "display",
              label: "Anzeige",
              components: [
                {
                  type: "textfield",
                  key: "label",
                  label: "Label"
                },
                {
                  type: "textfield",
                  key: "key",
                  label: "API-Key",
                  validate: {
                    required: true
                  }
                },
                {
                  type: "checkbox",
                  key: "persistent",
                  label:
                    "In Formulardaten speichern",
                  defaultValue: false
                },
                {
                  type: "datagrid",
                  key: "columns",
                  label: "Spalten",
                  reorder: true,
                  components: [
                    {
                      type: "textfield",
                      key: "label",
                      label: "Überschrift",
                      validate: {
                        required: true
                      }
                    },
                    {
                      type: "textfield",
                      key: "field",
                      label: "DTO-Feld",
                      validate: {
                        required: true
                      }
                    },
                    {
                      type: "checkbox",
                      key: "useEditOptionsForDisplay",
                      label: "Bearbeitungswerte als Labels anzeigen",
                      defaultValue: false,
                      description:
                        "Verwendet die konfigurierten Bearbeitungswerte nur für die Anzeige dieser Spalte. Die technischen Werte im Datenmodell bleiben unverändert."
                    },
                    {
                      type: "textfield",
                      key: "emptyDisplayLabel",
                      label: "Anzeigetext für leeren Wert",
                      placeholder: "Keine Abweichung",
                      description:
                        "Optional. Wird angezeigt, wenn das DTO-Feld null, undefined oder leer ist."
                    }
                  ]
                },
                {
                  type: "checkbox",
                  key: "editable",
                  label: "Bearbeitbar"
                },
                {
                  type: "textfield",
                  key: "editField",
                  label:
                    "Bearbeitbares DTO-Feld",
                  defaultValue:
                    "permissionLevel"
                },
                {
                  type: "textfield",
                  key: "editLabel",
                  label:
                    "Überschrift Bearbeitung",
                  defaultValue:
                    "Bearbeitung"
                }
              ]
            },
            {
              key: "tableFunctions",
              label: "Tabellenfunktionen",
              components: [
                {
                  type: "checkbox",
                  key: "searchEnabled",
                  label: "Suche aktivieren",
                  defaultValue: false
                },
                {
                  type: "checkbox",
                  key: "reloadOnDataChanged",
                  label: "Nach Datenänderungen anderer API-Komponenten neu laden",
                  defaultValue: false,
                  description:
                    "Lädt die API-Daten neu, wenn eine andere API-Komponente das neutrale Datenänderungsereignis auslöst."
                },
                {
                  type: "textfield",
                  key: "searchPlaceholder",
                  label:
                    "Platzhalter der Suche",
                  defaultValue:
                    "Einträge durchsuchen"
                },
                {
                  type: "textfield",
                  key: "searchFieldsText",
                  label:
                    "Durchsuchte DTO-Felder",
                  placeholder:
                    "label, componentPath, permissionKey",
                  description:
                    "Kommagetrennte Liste. Leer bedeutet: alle konfigurierten Spalten durchsuchen."
                },
                {
                  type: "checkbox",
                  key: "paginationEnabled",
                  label:
                    "Pagination aktivieren",
                  defaultValue: true
                },
                {
                  type: "number",
                  key: "pageSize",
                  label:
                    "Einträge pro Seite",
                  defaultValue: 10,
                  validate: {
                    min: 1,
                    integer: true
                  }
                },
                {
                  type: "textfield",
                  key: "pageSizeOptionsText",
                  label:
                    "Auswählbare Seitengrößen",
                  defaultValue:
                    "5, 10, 25, 50"
                },
                {
                  type: "checkbox",
                  key: "sortingEnabled",
                  label:
                    "Sortierung über Spaltenüberschriften aktivieren",
                  defaultValue: true
                },
                {
                  type: "textfield",
                  key: "defaultSortField",
                  label:
                    "Standard-Sortierfeld",
                  placeholder:
                    "label"
                },
                {
                  type: "select",
                  key: "defaultSortDirection",
                  label:
                    "Standard-Sortierrichtung",
                  data: {
                    values: [
                      {
                        label:
                          "Aufsteigend",
                        value: "asc"
                      },
                      {
                        label:
                          "Absteigend",
                        value: "desc"
                      }
                    ]
                  },
                  defaultValue: "asc"
                }
              ]
            },
            {
              key: "filters",
              label: "Filter",
              components: [
                {
                  type: "checkbox",
                  key: "filtersEnabled",
                  label: "Filter aktivieren",
                  defaultValue: false
                },
                {
                  type: "checkbox",
                  key: "filtersCollapsible",
                  label:
                    "Filterbereich ein- und ausklappbar",
                  defaultValue: true
                },
                {
                  type: "checkbox",
                  key: "filtersInitiallyOpen",
                  label:
                    "Filterbereich standardmäßig geöffnet",
                  defaultValue: false
                },
                {
                  type: "checkbox",
                  key: "showResetFiltersButton",
                  label:
                    "Schaltfläche „Filter zurücksetzen“ anzeigen",
                  defaultValue: true
                },
                {
                  type: "editgrid",
                  key: "filterDefinitions",
                  label: "Filterdefinitionen",
                  addAnother:
                    "Filter hinzufügen",
                  saveRow:
                    "Filter übernehmen",
                  removeRow:
                    "Abbrechen",
                  components: [
                    {
                      type: "textfield",
                      key: "key",
                      label:
                        "Technischer Filterschlüssel",
                      validate: {
                        required: true
                      }
                    },
                    {
                      type: "textfield",
                      key: "field",
                      label: "DTO-Feld",
                      validate: {
                        required: true
                      }
                    },
                    {
                      type: "textfield",
                      key: "label",
                      label: "Bezeichnung",
                      validate: {
                        required: true
                      }
                    },
                    {
                      type: "select",
                      key: "type",
                      label: "Filtertyp",
                      data: {
                        values: [
                          {
                            label: "Text",
                            value: "text"
                          },
                          {
                            label: "Auswahlliste",
                            value: "select"
                          },
                          {
                            label:
                              "Mehrfachauswahl",
                            value:
                              "multiselect"
                          },
                          {
                            label: "Ja/Nein",
                            value: "boolean"
                          },
                          {
                            label: "Zahl",
                            value: "number"
                          },
                          {
                            label:
                              "Zahlenbereich",
                            value:
                              "numberRange"
                          },
                          {
                            label: "Datum",
                            value: "date"
                          },
                          {
                            label:
                              "Datumsbereich",
                            value:
                              "dateRange"
                          }
                        ]
                      },
                      defaultValue: "text"
                    },
                    {
                      type: "select",
                      key: "operator",
                      label:
                        "Textvergleich",
                      data: {
                        values: [
                          {
                            label: "Enthält",
                            value: "contains"
                          },
                          {
                            label:
                              "Beginnt mit",
                            value:
                              "startsWith"
                          },
                          {
                            label:
                              "Endet mit",
                            value:
                              "endsWith"
                          },
                          {
                            label:
                              "Ist gleich",
                            value:
                              "equals"
                          },
                          {
                            label:
                              "Ist nicht gleich",
                            value:
                              "notEquals"
                          }
                        ]
                      },
                      defaultValue:
                        "contains"
                    },
                    {
                      type: "textfield",
                      key: "placeholder",
                      label:
                        "Platzhalter"
                    },
                    {
                      type: "textfield",
                      key: "valuesText",
                      label:
                        "Feste Auswahlwerte",
                      description:
                        "Kommagetrennte Werte. Leer bedeutet: Werte automatisch aus den geladenen Zeilen bilden."
                    },
                    {
                      type: "checkbox",
                      key: "ignoreCase",
                      label:
                        "Groß-/Kleinschreibung ignorieren",
                      defaultValue: true
                    },
                    {
                      type: "textfield",
                      key: "fromLabel",
                      label:
                        "Beschriftung Untergrenze",
                      defaultValue:
                        "Mindestens"
                    },
                    {
                      type: "textfield",
                      key: "toLabel",
                      label:
                        "Beschriftung Obergrenze",
                      defaultValue:
                        "Höchstens"
                    },
                    {
                      type: "textfield",
                      key: "fromTitle",
                      label:
                        "Tooltip Untergrenze"
                    },
                    {
                      type: "textfield",
                      key: "toTitle",
                      label:
                        "Tooltip Obergrenze"
                    }
                  ]
                }
              ]
            },
            {
              key: "changes",
              label: "Änderungen",
              components: [
                {
                  type: "checkbox",
                  key: "showDirtyCount",
                  label:
                    "Anzahl offener Änderungen anzeigen",
                  defaultValue: true
                },
                {
                  type: "checkbox",
                  key: "showResetChangesButton",
                  label:
                    "Schaltfläche „Änderungen verwerfen“ anzeigen",
                  defaultValue: true
                },
                {
                  type: "checkbox",
                  key: "highlightChangedRows",
                  label:
                    "Geänderte Zeilen hervorheben",
                  defaultValue: true
                },
                {
                  type: "textfield",
                  key: "saveButtonLabel",
                  label:
                    "Text der Speichern-Schaltfläche",
                  defaultValue:
                    "Änderungen speichern"
                },
                {
                  type: "textfield",
                  key:
                    "resetChangesButtonLabel",
                  label:
                    "Text der Zurücksetzen-Schaltfläche",
                  defaultValue:
                    "Änderungen verwerfen"
                }
              ]
            },
            {
              key: "messages",
              label: "Meldungen",
              components: [
                {
                  type: "textfield",
                  key: "emptyMessage",
                  label:
                    "Text bei leerer API-Antwort",
                  defaultValue:
                    "Keine Einträge vorhanden."
                },
                {
                  type: "textfield",
                  key:
                    "noFilterResultMessage",
                  label:
                    "Text ohne Such-/Filtertreffer",
                  defaultValue:
                    "Keine passenden Einträge gefunden."
                },
                {
                  type: "textfield",
                  key: "loadingMessage",
                  label: "Ladetext",
                  defaultValue:
                    "Daten werden geladen …"
                },
                {
                  type: "textfield",
                  key: "savingMessage",
                  label: "Speichertext",
                  defaultValue:
                    "Änderungen werden gespeichert …"
                }
              ]
            },
            {
              key: "apiLoad",
              label: "API: Laden",
              components: [
                {
                  type: "textfield",
                  key:
                    "api.loadEndpoint",
                  label:
                    "Lade-Endpoint",
                  placeholder:
                    "/api/items/{targetType}/{targetId}",
                  validate: {
                    required: true
                  }
                },
                {
                  type: "select",
                  key:
                    "api.loadMethod",
                  label:
                    "Lademethode",
                  data: {
                    values: [
                      {
                        label: "GET",
                        value: "GET"
                      },
                      {
                        label: "POST",
                        value: "POST"
                      }
                    ]
                  },
                  defaultValue: "GET"
                },
                {
                  type: "textfield",
                  key:
                    "api.resultPath",
                  label:
                    "Pfad zum Ergebnis-Array",
                  placeholder:
                    "items"
                },
                {
                  type: "textfield",
                  key:
                    "api.dependsOnText",
                  label:
                    "Abhängige Form.io-Felder",
                  placeholder:
                    "targetType, targetId",
                  description:
                    "Mehrere Feldschlüssel durch Komma trennen. Die API wird erst geladen, wenn alle Felder einen Wert besitzen."
                },
                {
                  type: "datagrid",
                  key: "api.queryParams",
                  label: "Query-Parameter",
                  reorder: true,
                  description: "Optionale Query-Parameter aus Formular-, Gemeinde-, Benutzer- oder globalem Window-Kontext.",
                  components: [
                    {
                      type: "textfield",
                      key: "name",
                      label: "Parametername",
                      placeholder: "communityIdentifier",
                      validate: { required: true }
                    },
                    {
                      type: "select",
                      key: "source",
                      label: "Quelle",
                      data: {
                        values: [
                          { label: "Formulardaten", value: "form" },
                          { label: "Gemeindekontext", value: "community" },
                          { label: "Benutzerkontext", value: "currentUser" },
                          { label: "Window / global", value: "window" },
                          { label: "Fester Wert", value: "literal" }
                        ]
                      },
                      defaultValue: "form"
                    },
                    {
                      type: "textfield",
                      key: "path",
                      label: "Pfad",
                      placeholder: "communityIdentifier"
                    },
                    {
                      type: "textfield",
                      key: "value",
                      label: "Fester Wert",
                      placeholder: "active"
                    },
                    {
                      type: "checkbox",
                      key: "required",
                      label: "Pflichtparameter",
                      defaultValue: false
                    }
                  ]
                },
                {
                  type: "checkbox",
                  key:
                    "api.autoLoad",
                  label:
                    "Automatisch laden",
                  defaultValue: true
                }
              ]
            },
            {
              key: "contextMappings",
              label: "Feldzuordnungen",
              components: [
                {
                  type: "content",
                  key:
                    "contextMappingsHint",
                  html:
                    "<p>Mit Feldzuordnungen können Werte für API-Platzhalter aus anderen Form.io-Feldern abgeleitet werden.</p>",
                  input: false
                },
                {
                  type: "datagrid",
                  key:
                    "api.contextMappings",
                  label:
                    "Kontext-Feldzuordnungen",
                  reorder: true,
                  components: [
                    {
                      type: "textfield",
                      key: "outputField",
                      label: "Zielfeld",
                      validate: {
                        required: true
                      }
                    },
                    {
                      type: "textfield",
                      key: "sourceField",
                      label: "Quellfeld",
                      validate: {
                        required: true
                      }
                    },
                    {
                      type: "textfield",
                      key: "whenField",
                      label:
                        "Bedingungsfeld"
                    },
                    {
                      type: "textfield",
                      key: "whenValue",
                      label:
                        "Bedingungswert"
                    },
                    {
                      type: "checkbox",
                      key: "ignoreCase",
                      label:
                        "Groß-/Kleinschreibung ignorieren",
                      defaultValue: true
                    },
                    {
                      type: "checkbox",
                      key: "onlyWhenEmpty",
                      label:
                        "Nur schreiben, wenn Zielfeld leer ist",
                      defaultValue: true
                    },
                    {
                      type: "checkbox",
                      key: "copyEmptyValue",
                      label:
                        "Auch leere Werte übernehmen",
                      defaultValue: false
                    }
                  ]
                }
              ]
            },
            {
              key: "apiSave",
              label: "API: Speichern",
              components: [
                {
                  type: "textfield",
                  key:
                    "api.saveEndpoint",
                  label:
                    "Speicher-Endpoint",
                  placeholder:
                    "/api/items/{targetType}/{targetId}"
                },
                {
                  type: "select",
                  key:
                    "api.saveMethod",
                  label:
                    "Speichermethode",
                  data: {
                    values: [
                      {
                        label: "PUT",
                        value: "PUT"
                      },
                      {
                        label: "POST",
                        value: "POST"
                      },
                      {
                        label: "PATCH",
                        value: "PATCH"
                      }
                    ]
                  },
                  defaultValue: "PUT"
                },
                {
                  type: "number",
                  key: "api.nullValue",
                  label:
                    "Wert für keine Auswahl",
                  defaultValue: -1
                },
                {
                  type: "textfield",
                  key:
                    "api.savePayloadPath",
                  label:
                    "Payload-Pfad für Zeilen",
                  placeholder:
                    "items"
                },
                {
                  type: "textfield",
                  key:
                    "api.saveContextFieldsText",
                  label:
                    "Zusätzliche Kontextfelder im Payload",
                  placeholder:
                    "targetType, targetId"
                }
              ]
            },
            {
              key: "editOptions",
              label:
                "Bearbeitungswerte",
              components: [
                {
                  type: "datagrid",
                  key: "editOptions",
                  label: "Auswahlwerte",
                  reorder: true,
                  components: [
                    {
                      type: "textfield",
                      key: "label",
                      label: "Label",
                      validate: {
                        required: false
                      }
                    },
                    {
                      type: "number",
                      key: "value",
                      label: "Wert",
                      validate: {
                        required: false
                      }
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    };
  };

installDeferredApiLoading(ApiDataGridComponent, ['loadRows']);

Formio.Components.addComponent(
  "apiDataGrid",
  ApiDataGridComponent
);